import { memo } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";

const components: Components = {
  a({ href, children, ...rest }) {
    return (
      <a
        href={href ?? ""}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2 hover:text-primary/80"
        {...rest}
      >
        {children}
      </a>
    );
  },
  h1({ children }) {
    return <p className="text-sm font-bold mt-2 mb-1">{children}</p>;
  },
  h2({ children }) {
    return <p className="text-sm font-bold mt-2 mb-1">{children}</p>;
  },
  h3({ children }) {
    return <p className="text-sm font-semibold mt-1.5 mb-0.5">{children}</p>;
  },
  p({ children }) {
    return <p className="mb-1.5 last:mb-0">{children}</p>;
  },
  ul({ children }) {
    return <ul className="mb-1.5 ml-4 list-disc space-y-0.5 last:mb-0">{children}</ul>;
  },
  ol({ children }) {
    return <ol className="mb-1.5 ml-4 list-decimal space-y-0.5 last:mb-0">{children}</ol>;
  },
  li({ children }) {
    return <li className="text-sm">{children}</li>;
  },
  strong({ children }) {
    return <strong className="font-semibold">{children}</strong>;
  },
  blockquote({ children }) {
    return <blockquote className="border-l-2 border-muted-foreground/30 pl-3 italic text-muted-foreground my-1.5">{children}</blockquote>;
  },
  code({ children, className }) {
    const isBlock = className?.includes("language-");
    if (isBlock) {
      return (
        <pre className="my-1.5 overflow-x-auto rounded-md bg-muted p-2 text-xs">
          <code>{children}</code>
        </pre>
      );
    }
    return <code className="rounded bg-muted px-1 py-0.5 text-xs font-mono">{children}</code>;
  },
  pre({ children }) {
    return <>{children}</>;
  },
  table({ children }) {
    return (
      <div className="my-1.5 overflow-x-auto">
        <table className="min-w-full text-xs border-collapse">{children}</table>
      </div>
    );
  },
  th({ children }) {
    return <th className="border-b border-muted-foreground/20 px-2 py-1 text-left font-semibold">{children}</th>;
  },
  td({ children }) {
    return <td className="border-b border-muted-foreground/10 px-2 py-1">{children}</td>;
  },
};

export const MarkdownBody = memo(
  ({ content }: { content: string }) => {
    return (
      <div className="markdown-body text-sm leading-relaxed">
        <Markdown remarkPlugins={[remarkGfm]} components={components}>
          {content}
        </Markdown>
      </div>
    );
  },
  (prev, next) => prev.content === next.content,
);

MarkdownBody.displayName = "MarkdownBody";
